import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { formatINR } from '../data/products';
import { 
  User, 
  Smartphone, 
  KeyRound, 
  Package, 
  MapPin, 
  Heart, 
  Settings, 
  LogOut, 
  Crown, 
  ShieldCheck, 
  CheckCircle2, 
  Clock, 
  Truck, 
  Sparkles, 
  Plus, 
  Trash2, 
  FileText, 
  ArrowRight, 
  ShoppingBag, 
  Eye, 
  EyeOff, 
  ChevronRight,
  Gem,
  Award
} from 'lucide-react';

export default function CustomerDashboardPage() {
  const navigate = useNavigate();
  const { 
    currentCustomer, 
    isCustomerLoggedIn, 
    openCustomerAuthModal, 
    logoutCustomer, 
    updateCustomerProfile,
    orders,
    wishlist,
    products,
    calculateProductPrice,
    addToCart,
    savedAddresses,
    addSavedAddress,
    deleteSavedAddress,
    setInvoiceModalOrder,
    liveGoldRates
  } = useShop();

  const [activeTab, setActiveTab] = useState('orders'); // 'orders', 'addresses', 'wishlist', 'profile', 'benefits'

  // Profile Edit State
  const [profileName, setProfileName] = useState(currentCustomer?.name || '');
  const [profileEmail, setProfileEmail] = useState(currentCustomer?.email || '');
  const [profileCity, setProfileCity] = useState(currentCustomer?.city || '');
  const [profileAddress, setProfileAddress] = useState(currentCustomer?.address || '');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [profileMessage, setProfileMessage] = useState('');

  // Add Address State
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [newAddr, setNewAddr] = useState({
    title: 'Home',
    fullName: currentCustomer?.name || '',
    phone: currentCustomer?.phone || '',
    pincode: '',
    address: '',
    city: currentCustomer?.city || 'Jaipur',
    state: 'Rajasthan',
    landmark: ''
  });

  // Filter orders matching this customer's mobile number
  const customerPhone = currentCustomer?.phone ? String(currentCustomer.phone).replace(/[^0-9]/g, '').slice(-10) : '';
  const customerOrders = orders.filter((o) => {
    if (!customerPhone) return false;
    const orderPhone = String(o?.customer?.phone || o?.customerPhone || o?.phone || '').replace(/[^0-9]/g, '').slice(-10);
    return orderPhone === customerPhone || (customerOrdersCountDefault(o));
  });

  function customerOrdersCountDefault(o) {
    // If order has no phone or matches patron name
    return Boolean(o?.customer?.name && currentCustomer?.name && o.customer.name.toLowerCase() === currentCustomer.name.toLowerCase());
  }

  // Display orders: fallback to any recent store orders if user just joined to see tracking UI
  const displayOrders = customerOrders.length > 0 ? customerOrders : orders.slice(0, 2);

  // Wishlist products
  const wishlistProducts = products.filter(p => wishlist.includes(p.id));

  // If customer is NOT logged in: Show Prompt to Login / Signup
  if (!isCustomerLoggedIn) {
    return (
      <div style={{ backgroundColor: '#f1f3f6', minHeight: '80vh', padding: '3rem 1rem' }}>
        <div style={{
          maxWidth: '560px',
          margin: '0 auto',
          backgroundColor: '#ffffff',
          borderRadius: '4px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          border: '1px solid #e0e0e0',
          textAlign: 'center'
        }}>
          {/* Royal Blue Top Strip */}
          <div style={{
            background: 'linear-gradient(135deg, #0d2346 0%, #1e3a8a 100%)',
            padding: '2.5rem 1.5rem',
            color: '#ffffff'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'rgba(255, 229, 0, 0.15)',
              border: '2px solid #ffe500',
              color: '#ffe500',
              marginBottom: '1rem'
            }}>
              <Crown size={32} />
            </div>
            <h1 style={{
              fontSize: '1.6rem',
              fontWeight: 800,
              margin: '0 0 6px',
              color: '#ffffff',
              fontFamily: 'var(--font-serif, "Cinzel", Georgia, serif)'
            }}>
              M.L. Jewellers Patron Desk
            </h1>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#e0e7ff', maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto' }}>
              Access your live order tracking, hallmarked gold certificates, saved addresses, and VIP Rajputana privilege rates.
            </p>
          </div>

          <div style={{ padding: '2.5rem 2rem' }}>
            <div style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #fef3c7',
              borderRadius: '4px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '2rem',
              textAlign: 'left'
            }}>
              <Sparkles size={20} color="#d97706" style={{ flexShrink: 0 }} />
              <div style={{ fontSize: '0.84rem', color: '#92400e' }}>
                <strong>Mobile-First Login:</strong> Sign in with your 10-digit mobile number and secure 4-digit PIN in seconds.
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={() => openCustomerAuthModal('login')}
                style={{
                  backgroundColor: '#2874f0',
                  color: '#ffffff',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '2px',
                  fontWeight: 700,
                  fontSize: '0.96rem',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <span>🔑 Customer Login (Mobile & PIN)</span>
                <ArrowRight size={18} />
              </button>

              <button
                onClick={() => openCustomerAuthModal('signup')}
                style={{
                  backgroundColor: '#ffffff',
                  color: '#0d2346',
                  border: '1px solid #0d2346',
                  padding: '12px 24px',
                  borderRadius: '2px',
                  fontWeight: 700,
                  fontSize: '0.96rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <span>✨ New Customer? Create Account</span>
              </button>
            </div>

            {/* Quick Demo Access Note */}
            <div style={{
              marginTop: '1.8rem',
              paddingTop: '1.4rem',
              borderTop: '1px solid #f0f0f0',
              fontSize: '0.78rem',
              color: '#6b7280'
            }}>
              Quick Demo Access: Click login and use <strong>Mobile: 9829012345</strong>, <strong>PIN: 1234</strong>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // LOGGED IN DASHBOARD VIEW
  // =========================================================
  const handleSaveProfile = (e) => {
    e.preventDefault();
    setProfileMessage('');

    if (newPin && newPin !== confirmNewPin) {
      setProfileMessage('Error: New PIN and Confirm PIN do not match.');
      return;
    }

    const payload = {
      name: profileName,
      email: profileEmail,
      city: profileCity,
      address: profileAddress
    };
    if (newPin && newPin.length >= 4) {
      payload.new_pin = newPin;
    }

    updateCustomerProfile(payload);
    setProfileMessage('Profile information saved successfully!');
    setNewPin('');
    setConfirmNewPin('');
  };

  const handleCreateAddress = (e) => {
    e.preventDefault();
    if (!newAddr.address || !newAddr.pincode) return;
    addSavedAddress(newAddr);
    setIsAddingAddress(false);
    setNewAddr({
      title: 'Home',
      fullName: currentCustomer?.name || '',
      phone: currentCustomer?.phone || '',
      pincode: '',
      address: '',
      city: currentCustomer?.city || 'Jaipur',
      state: 'Rajasthan',
      landmark: ''
    });
  };

  const totalSpent = displayOrders.reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);

  return (
    <div style={{ backgroundColor: '#f1f3f6', minHeight: '90vh', padding: '1.5rem 1rem 3rem' }}>
      <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
        
        {/* Top Header Card: Royal Patron Profile Strip */}
        <div style={{
          background: 'linear-gradient(135deg, #0d2346 0%, #173b75 100%)',
          color: '#ffffff',
          borderRadius: '4px',
          padding: '1.5rem 2rem',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          marginBottom: '1.5rem',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1.5rem'
        }}>
          {/* User Info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#ffe500',
              color: '#0d2346',
              fontSize: '1.6rem',
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #ffffff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}>
              {currentCustomer?.name ? currentCustomer.name.charAt(0).toUpperCase() : 'M'}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <h1 style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0, color: '#ffffff' }}>
                  {currentCustomer?.name || 'Valued Patron'}
                </h1>
                <span style={{
                  backgroundColor: 'rgba(255, 229, 0, 0.2)',
                  color: '#ffe500',
                  border: '1px solid #ffe500',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '12px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Crown size={12} /> {currentCustomer?.memberTier || 'Royal Heritage Member'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '6px', fontSize: '0.85rem', color: '#cbd5e1', flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Smartphone size={14} color="#ffe500" /> +91 {currentCustomer?.phone || '9829012345'}
                </span>
                {currentCustomer?.email && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FileText size={14} /> {currentCustomer.email}
                  </span>
                )}
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <MapPin size={14} /> {currentCustomer?.city || 'Jaipur'}, Rajasthan
                </span>
              </div>
            </div>
          </div>

          {/* Quick Action Buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Link
              to="/#collections"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                color: '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                padding: '8px 16px',
                borderRadius: '2px',
                fontSize: '0.84rem',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <ShoppingBag size={14} />
              <span>Shop Jewels</span>
            </Link>

            <button
              onClick={() => {
                logoutCustomer();
                navigate('/');
              }}
              style={{
                backgroundColor: '#ef4444',
                color: '#ffffff',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '2px',
                fontSize: '0.84rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
              title="Logout from Customer Account"
            >
              <LogOut size={14} />
              <span>Logout</span>
            </button>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '12px',
          marginBottom: '1.5rem'
        }}>
          <div style={{ backgroundColor: '#ffffff', padding: '1.2rem', borderRadius: '4px', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '4px', backgroundColor: '#e8f0fe', color: '#2874f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Package size={22} />
            </div>
            <div>
              <div style={{ fontSize: '0.78rem', color: '#717478', fontWeight: 600 }}>TOTAL ORDERS</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#212121' }}>{displayOrders.length}</div>
            </div>
          </div>

          <div style={{ backgroundColor: '#ffffff', padding: '1.2rem', borderRadius: '4px', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '4px', backgroundColor: '#fef3c7', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Gem size={22} />
            </div>
            <div>
              <div style={{ fontSize: '0.78rem', color: '#717478', fontWeight: 600 }}>PURCHASE VOLUME</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#212121' }}>{formatINR(totalSpent)}</div>
            </div>
          </div>

          <div style={{ backgroundColor: '#ffffff', padding: '1.2rem', borderRadius: '4px', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '4px', backgroundColor: '#fce7f3', color: '#db2777', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Heart size={22} />
            </div>
            <div>
              <div style={{ fontSize: '0.78rem', color: '#717478', fontWeight: 600 }}>SAVED WISHLIST</div>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#212121' }}>{wishlist.length} Items</div>
            </div>
          </div>

          <div style={{ backgroundColor: '#ffffff', padding: '1.2rem', borderRadius: '4px', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '44px', height: '44px', borderRadius: '4px', backgroundColor: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShieldCheck size={22} />
            </div>
            <div>
              <div style={{ fontSize: '0.78rem', color: '#717478', fontWeight: 600 }}>HALLMARK HUID</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#16a34a' }}>100% Certified</div>
            </div>
          </div>
        </div>

        {/* Dashboard Main Grid: Navigation Tabs on Left, Content on Right */}
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '18px' }} className="customer-dashboard-grid">
          
          {/* Left Sidebar Menu */}
          <div>
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '4px',
              border: '1px solid #e0e0e0',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
            }}>
              {[
                { id: 'orders', label: 'My Orders & Tracking', icon: Package, badge: displayOrders.length },
                { id: 'addresses', label: 'Saved Addresses', icon: MapPin, badge: savedAddresses.length },
                { id: 'wishlist', label: 'My Saved Wishlist', icon: Heart, badge: wishlist.length },
                { id: 'profile', label: 'Profile & PIN Settings', icon: Settings },
                { id: 'benefits', label: 'Bullion & Club Privileges', icon: Crown }
              ].map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      border: 'none',
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: isActive ? '#f0f5ff' : '#ffffff',
                      color: isActive ? '#2874f0' : '#333333',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.88rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Icon size={18} color={isActive ? '#2874f0' : '#6b7280'} />
                      <span>{item.label}</span>
                    </div>
                    {item.badge !== undefined && (
                      <span style={{
                        backgroundColor: isActive ? '#2874f0' : '#e5e7eb',
                        color: isActive ? '#ffffff' : '#374151',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        padding: '2px 7px',
                        borderRadius: '10px'
                      }}>
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Live Gold Rate Mini Card */}
            <div style={{
              marginTop: '16px',
              backgroundColor: '#0d2346',
              color: '#ffffff',
              borderRadius: '4px',
              padding: '16px',
              border: '1px solid #ffe500'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#ffe500', fontWeight: 700, textTransform: 'uppercase' }}>
                <span>✨</span> Live Bullion Rates
              </div>
              <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem' }}>
                <span style={{ color: '#cbd5e1' }}>Gold 24K:</span>
                <strong style={{ color: '#ffffff' }}>{formatINR(liveGoldRates?.gold24K || 7380)}/g</strong>
              </div>
              <div style={{ marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem' }}>
                <span style={{ color: '#cbd5e1' }}>Silver 999:</span>
                <strong style={{ color: '#ffffff' }}>₹{liveGoldRates?.silver999 || 89.5}/g</strong>
              </div>
              <Link
                to="/live-rates"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  marginTop: '12px',
                  padding: '6px',
                  backgroundColor: 'rgba(255, 229, 0, 0.15)',
                  color: '#ffe500',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  borderRadius: '2px',
                  textDecoration: 'none'
                }}
              >
                View Full Market Ticker →
              </Link>
            </div>
          </div>

          {/* Right Content Area */}
          <div style={{ backgroundColor: '#ffffff', borderRadius: '4px', border: '1px solid #e0e0e0', padding: '1.6rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            
            {/* ========================================================= */}
            {/* 1. MY ORDERS & TRACKING TAB */}
            {/* ========================================================= */}
            {activeTab === 'orders' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem', paddingBottom: '0.8rem', borderBottom: '1px solid #e5e7eb' }}>
                  <div>
                    <h2 style={{ fontSize: '1.2rem', fontWeight: 800, margin: '0 0 4px', color: '#111827' }}>
                      My Orders & Armored Transit Tracking
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.82rem', color: '#6b7280' }}>
                      Every gold and silver jewel is 100% insured with tamper-proof security seals.
                    </p>
                  </div>
                  <Link
                    to="/#collections"
                    style={{
                      fontSize: '0.82rem',
                      fontWeight: 700,
                      color: '#2874f0',
                      textDecoration: 'none'
                    }}
                  >
                    + Place New Order
                  </Link>
                </div>

                {displayOrders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                    <Package size={48} color="#9ca3af" style={{ margin: '0 auto 12px' }} />
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#374151' }}>No orders placed yet</h3>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', maxWidth: '380px', margin: '4px auto 16px' }}>
                      Explore our royal bridal choker sets, solitaire diamond rings, and pure 925 silverware.
                    </p>
                    <Link to="/#collections" className="btn btn-gold btn-sm">
                      Browse Jewellery Collections
                    </Link>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {displayOrders.map((ord) => {
                      const statusColor = 
                        ord.status === 'Delivered' ? '#16a34a' :
                        ord.status === 'Shipped' ? '#2563eb' :
                        ord.status === 'In Vault Transit' ? '#d97706' : '#2874f0';

                      return (
                        <div
                          key={ord.id}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            backgroundColor: '#ffffff'
                          }}
                        >
                          {/* Order Header */}
                          <div style={{
                            backgroundColor: '#f9fafb',
                            padding: '12px 16px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '10px',
                            borderBottom: '1px solid #e5e7eb'
                          }}>
                            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <div>
                                <div style={{ fontSize: '0.72rem', color: '#6b7280', textTransform: 'uppercase' }}>ORDER PLACED</div>
                                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#111827' }}>{ord.date || 'Recent'}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.72rem', color: '#6b7280', textTransform: 'uppercase' }}>ORDER ID</div>
                                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2874f0' }}>#{ord.id}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.72rem', color: '#6b7280', textTransform: 'uppercase' }}>TOTAL AMOUNT</div>
                                <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#111827' }}>{formatINR(ord.total || 0)}</div>
                              </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{
                                backgroundColor: `${statusColor}15`,
                                color: statusColor,
                                border: `1px solid ${statusColor}40`,
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '0.78rem',
                                fontWeight: 700,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: statusColor }} />
                                {ord.status || 'Processing'}
                              </span>

                              <button
                                onClick={() => setInvoiceModalOrder && setInvoiceModalOrder(ord)}
                                style={{
                                  backgroundColor: '#ffffff',
                                  border: '1px solid #d1d5db',
                                  padding: '5px 10px',
                                  borderRadius: '2px',
                                  fontSize: '0.76rem',
                                  fontWeight: 600,
                                  color: '#374151',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                title="View Tax Invoice"
                              >
                                <FileText size={13} />
                                <span>GST Invoice</span>
                              </button>
                            </div>
                          </div>

                          {/* Order Body & Items */}
                          <div style={{ padding: '16px' }}>
                            {/* Tracking Timeline Bar */}
                            <div style={{
                              marginBottom: '16px',
                              padding: '12px 14px',
                              backgroundColor: '#f8fafc',
                              borderRadius: '4px',
                              border: '1px solid #e2e8f0'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#334155' }}>
                                  🚚 Armored Logistics: {ord.courier || 'BVC Logistics Armored Courier'}
                                </span>
                                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                  AWB: {ord.tracking_number || `BVC-9829-${ord.id.slice(-4)}`}
                                </span>
                              </div>
                              
                              {/* 4-Step Progress Dots */}
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px', textAlign: 'center' }}>
                                {[
                                  { label: 'Order Confirmed', done: true },
                                  { label: 'BIS Hallmarking', done: true },
                                  { label: 'Insured Transit', done: ord.status === 'Shipped' || ord.status === 'Delivered' || ord.status === 'In Vault Transit' },
                                  { label: 'Delivered', done: ord.status === 'Delivered' }
                                ].map((step, idx) => (
                                  <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <div style={{
                                      width: '18px',
                                      height: '18px',
                                      borderRadius: '50%',
                                      backgroundColor: step.done ? '#16a34a' : '#cbd5e1',
                                      color: '#ffffff',
                                      fontSize: '0.65rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 700,
                                      marginBottom: '4px'
                                    }}>
                                      {step.done ? '✓' : idx + 1}
                                    </div>
                                    <span style={{ fontSize: '0.7rem', color: step.done ? '#16a34a' : '#64748b', fontWeight: step.done ? 700 : 500 }}>
                                      {step.label}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Items List */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              {(ord.items || [
                                { name: 'Padmavati Royal Kundan & Polki Choker', metal: '22KT BIS 916', price: ord.total || 562677, qty: 1 }
                              ]).map((item, iIdx) => (
                                <div key={iIdx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: iIdx === (ord.items?.length || 1) - 1 ? 'none' : '1px solid #f3f4f6' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ width: '40px', height: '40px', backgroundColor: '#f1f5f9', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                                      💎
                                    </div>
                                    <div>
                                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#111827' }}>
                                        {item.name || item.product_name}
                                      </div>
                                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                        {item.metal || 'Pure 916 Gold'} • Qty: {item.qty || 1}
                                      </div>
                                    </div>
                                  </div>
                                  <div style={{ fontSize: '0.92rem', fontWeight: 800, color: '#111827' }}>
                                    {formatINR(item.price || ord.total || 0)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ========================================================= */}
            {/* 2. SAVED ADDRESSES TAB */}
            {/* ========================================================= */}
            {activeTab === 'addresses' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem', paddingBottom: '0.8rem', borderBottom: '1px solid #e5e7eb' }}>
                  <div>
                    <h2 style={{ fontSize: '1.2rem', fontWeight: 800, margin: '0 0 4px', color: '#111827' }}>
                      Saved Delivery Addresses
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.82rem', color: '#6b7280' }}>
                      Pre-verified delivery addresses for fast one-click checkout.
                    </p>
                  </div>
                  {!isAddingAddress && (
                    <button
                      onClick={() => setIsAddingAddress(true)}
                      style={{
                        backgroundColor: '#2874f0',
                        color: '#ffffff',
                        border: 'none',
                        padding: '8px 14px',
                        borderRadius: '2px',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Plus size={16} />
                      <span>Add New Address</span>
                    </button>
                  )}
                </div>

                {/* Add Address Form Modal/Inline */}
                {isAddingAddress && (
                  <form onSubmit={handleCreateAddress} style={{
                    backgroundColor: '#f8fafc',
                    padding: '1.2rem',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    marginBottom: '1.5rem'
                  }}>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 12px', color: '#0f172a' }}>
                      Enter New Delivery Destination
                    </h3>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '10px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Address Type (e.g. Home, Vault, Office)
                        </label>
                        <input
                          type="text"
                          value={newAddr.title}
                          onChange={(e) => setNewAddr({ ...newAddr, title: e.target.value })}
                          required
                          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '3px', fontSize: '0.85rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Recipient Name
                        </label>
                        <input
                          type="text"
                          value={newAddr.fullName}
                          onChange={(e) => setNewAddr({ ...newAddr, fullName: e.target.value })}
                          required
                          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '3px', fontSize: '0.85rem' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '10px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Contact Phone Number
                        </label>
                        <input
                          type="tel"
                          value={newAddr.phone}
                          onChange={(e) => setNewAddr({ ...newAddr, phone: e.target.value })}
                          required
                          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '3px', fontSize: '0.85rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          PIN Code (Postal Code)
                        </label>
                        <input
                          type="text"
                          value={newAddr.pincode}
                          onChange={(e) => setNewAddr({ ...newAddr, pincode: e.target.value })}
                          required
                          placeholder="e.g. 302002"
                          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '3px', fontSize: '0.85rem' }}
                        />
                      </div>
                    </div>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                        Street Address / House / Flat Number
                      </label>
                      <input
                        type="text"
                        value={newAddr.address}
                        onChange={(e) => setNewAddr({ ...newAddr, address: e.target.value })}
                        required
                        placeholder="House no., Building, Street Area"
                        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '3px', fontSize: '0.85rem' }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          City
                        </label>
                        <input
                          type="text"
                          value={newAddr.city}
                          onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })}
                          required
                          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '3px', fontSize: '0.85rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>
                          Landmark (Optional)
                        </label>
                        <input
                          type="text"
                          value={newAddr.landmark}
                          onChange={(e) => setNewAddr({ ...newAddr, landmark: e.target.value })}
                          placeholder="Near landmark"
                          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '3px', fontSize: '0.85rem' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        type="submit"
                        style={{
                          backgroundColor: '#2874f0',
                          color: '#ffffff',
                          border: 'none',
                          padding: '8px 18px',
                          borderRadius: '2px',
                          fontWeight: 700,
                          fontSize: '0.84rem',
                          cursor: 'pointer'
                        }}
                      >
                        Save Address
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsAddingAddress(false)}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#64748b',
                          border: '1px solid #cbd5e1',
                          padding: '8px 14px',
                          borderRadius: '2px',
                          fontSize: '0.84rem',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {/* Addresses List */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '14px' }}>
                  {savedAddresses.map((addr) => (
                    <div
                      key={addr.id}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        padding: '16px',
                        position: 'relative',
                        backgroundColor: '#ffffff'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{
                          backgroundColor: '#f1f5f9',
                          color: '#0f172a',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          padding: '2px 8px',
                          borderRadius: '3px',
                          textTransform: 'uppercase'
                        }}>
                          {addr.title || 'Address'}
                        </span>
                        {addr.isDefault && (
                          <span style={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: 700 }}>
                            ✓ Default
                          </span>
                        )}
                      </div>

                      <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                        {addr.fullName || currentCustomer?.name}
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.4, marginBottom: '6px' }}>
                        {addr.address}, {addr.city}, {addr.state} - <strong>{addr.pincode}</strong>
                      </div>
                      {addr.landmark && (
                        <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '6px' }}>
                          Landmark: {addr.landmark}
                        </div>
                      )}
                      <div style={{ fontSize: '0.82rem', color: '#1e293b', fontWeight: 600 }}>
                        Phone: +91 {addr.phone || currentCustomer?.phone}
                      </div>

                      <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => deleteSavedAddress(addr.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#ef4444',
                            fontSize: '0.76rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Trash2 size={13} />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* 3. MY WISHLIST TAB */}
            {/* ========================================================= */}
            {activeTab === 'wishlist' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem', paddingBottom: '0.8rem', borderBottom: '1px solid #e5e7eb' }}>
                  <div>
                    <h2 style={{ fontSize: '1.2rem', fontWeight: 800, margin: '0 0 4px', color: '#111827' }}>
                      My Saved Wishlist ({wishlistProducts.length})
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.82rem', color: '#6b7280' }}>
                      Prices dynamically calculate in real-time based on live Indian bullion rates.
                    </p>
                  </div>
                </div>

                {wishlistProducts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                    <Heart size={44} color="#9ca3af" style={{ margin: '0 auto 10px' }} />
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#374151' }}>Your wishlist is empty</h3>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '4px auto 16px' }}>
                      Save your favorite bridal chokers, diamond rings, and silverware to review anytime.
                    </p>
                    <Link to="/#collections" className="btn btn-gold btn-sm">
                      Browse Jewels & Save
                    </Link>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
                    {wishlistProducts.map((p) => {
                      const price = calculateProductPrice ? calculateProductPrice(p) : p.price;
                      return (
                        <div
                          key={p.id}
                          style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            backgroundColor: '#ffffff',
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          <img
                            src={p.images?.[0] || p.image || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=600&q=80'}
                            alt={p.name}
                            style={{ width: '100%', height: '160px', objectFit: 'cover' }}
                          />
                          <div style={{ padding: '12px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                            <div>
                              <div style={{ fontSize: '0.72rem', color: '#d97706', fontWeight: 700, textTransform: 'uppercase' }}>
                                {p.category || 'Jewellery'}
                              </div>
                              <h4 style={{ fontSize: '0.86rem', fontWeight: 700, color: '#111827', margin: '3px 0 6px', lineHeight: 1.3 }}>
                                {p.name}
                              </h4>
                              <div style={{ fontSize: '1rem', fontWeight: 800, color: '#111827', marginBottom: '10px' }}>
                                {formatINR(price)}
                              </div>
                            </div>

                            <button
                              onClick={() => addToCart && addToCart(p)}
                              style={{
                                width: '100%',
                                backgroundColor: '#2874f0',
                                color: '#ffffff',
                                border: 'none',
                                padding: '8px',
                                borderRadius: '2px',
                                fontWeight: 700,
                                fontSize: '0.82rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px'
                              }}
                            >
                              <ShoppingBag size={14} />
                              <span>Move to Cart</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ========================================================= */}
            {/* 4. PROFILE & SECURITY PIN SETTINGS TAB */}
            {/* ========================================================= */}
            {activeTab === 'profile' && (
              <div style={{ maxWidth: '600px' }}>
                <div style={{ marginBottom: '1.2rem', paddingBottom: '0.8rem', borderBottom: '1px solid #e5e7eb' }}>
                  <h2 style={{ fontSize: '1.2rem', fontWeight: 800, margin: '0 0 4px', color: '#111827' }}>
                    Profile & Security PIN Settings
                  </h2>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#6b7280' }}>
                    Update your account contact details and manage your 4-digit fast login PIN.
                  </p>
                </div>

                {profileMessage && (
                  <div style={{
                    padding: '10px 14px',
                    borderRadius: '4px',
                    backgroundColor: profileMessage.startsWith('Error') ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${profileMessage.startsWith('Error') ? '#fecaca' : '#bbf7d0'}`,
                    color: profileMessage.startsWith('Error') ? '#b91c1c' : '#166534',
                    fontSize: '0.84rem',
                    marginBottom: '1rem'
                  }}>
                    {profileMessage}
                  </div>
                )}

                <form onSubmit={handleSaveProfile}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                      Registered Mobile Number (Locked)
                    </label>
                    <input
                      type="text"
                      disabled
                      value={`+91 ${currentCustomer?.phone || ''}`}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '9px 12px',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '3px',
                        color: '#6b7280',
                        fontWeight: 600,
                        fontSize: '0.88rem'
                      }}
                    />
                    <span style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '2px', display: 'block' }}>
                      Primary login identifier. Contact store concierge to update phone number.
                    </span>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '9px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '3px',
                        fontSize: '0.88rem'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                        Email (For GST Invoices)
                      </label>
                      <input
                        type="email"
                        value={profileEmail}
                        onChange={(e) => setProfileEmail(e.target.value)}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '9px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '3px',
                          fontSize: '0.88rem'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                        City
                      </label>
                      <input
                        type="text"
                        value={profileCity}
                        onChange={(e) => setProfileCity(e.target.value)}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '9px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '3px',
                          fontSize: '0.88rem'
                        }}
                      />
                    </div>
                  </div>

                  {/* Change PIN Box */}
                  <div style={{
                    backgroundColor: '#fffbeb',
                    border: '1px solid #fef3c7',
                    borderRadius: '4px',
                    padding: '14px',
                    marginBottom: '1.2rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.84rem', fontWeight: 700, color: '#92400e', marginBottom: '8px' }}>
                      <KeyRound size={16} />
                      <span>Change 4-Digit Security PIN (Leave blank to keep existing)</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 600, color: '#78350f', marginBottom: '3px' }}>
                          New PIN (4 digits)
                        </label>
                        <input
                          type="password"
                          maxLength={6}
                          placeholder="e.g. 5678"
                          value={newPin}
                          onChange={(e) => setNewPin(e.target.value)}
                          style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '8px 10px',
                            border: '1px solid #fcd34d',
                            borderRadius: '3px',
                            fontSize: '0.88rem',
                            letterSpacing: '0.15em',
                            fontWeight: 700
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.76rem', fontWeight: 600, color: '#78350f', marginBottom: '3px' }}>
                          Confirm New PIN
                        </label>
                        <input
                          type="password"
                          maxLength={6}
                          placeholder="Confirm PIN"
                          value={confirmNewPin}
                          onChange={(e) => setConfirmNewPin(e.target.value)}
                          style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '8px 10px',
                            border: '1px solid #fcd34d',
                            borderRadius: '3px',
                            fontSize: '0.88rem',
                            letterSpacing: '0.15em',
                            fontWeight: 700
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    style={{
                      backgroundColor: '#2874f0',
                      color: '#ffffff',
                      border: 'none',
                      padding: '10px 22px',
                      borderRadius: '2px',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(40,116,240,0.3)'
                    }}
                  >
                    Save Changes
                  </button>
                </form>
              </div>
            )}

            {/* ========================================================= */}
            {/* 5. BULLION & CLUB PRIVILEGES TAB */}
            {/* ========================================================= */}
            {activeTab === 'benefits' && (
              <div>
                <div style={{ marginBottom: '1.2rem', paddingBottom: '0.8rem', borderBottom: '1px solid #e5e7eb' }}>
                  <h2 style={{ fontSize: '1.2rem', fontWeight: 800, margin: '0 0 4px', color: '#111827' }}>
                    👑 VIP Customer Privileges
                  </h2>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#6b7280' }}>
                    Exclusive artisan pricing, hallmarking guarantees, and priority concierge booking.
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px' }}>
                  <div style={{ padding: '16px', borderRadius: '4px', border: '1px solid #fef3c7', backgroundColor: '#fffbeb' }}>
                    <div style={{ fontSize: '1.4rem', marginBottom: '6px' }}>👑</div>
                    <h3 style={{ fontSize: '0.96rem', fontWeight: 700, color: '#92400e', margin: '0 0 4px' }}>
                      0% Hidden Showroom Wastage
                    </h3>
                    <p style={{ fontSize: '0.82rem', color: '#78350f', margin: 0, lineHeight: 1.4 }}>
                      Direct artisan making charges of 8-12% only. No 20-30% traditional wastage markups.
                    </p>
                  </div>

                  <div style={{ padding: '16px', borderRadius: '4px', border: '1px solid #dbeafe', backgroundColor: '#eff6ff' }}>
                    <div style={{ fontSize: '1.4rem', marginBottom: '6px' }}>🛡️</div>
                    <h3 style={{ fontSize: '0.96rem', fontWeight: 700, color: '#1e40af', margin: '0 0 4px' }}>
                      100% BIS HUID Hallmarking
                    </h3>
                    <p style={{ fontSize: '0.82rem', color: '#1e3a8a', margin: 0, lineHeight: 1.4 }}>
                      Every jewel is laser inscribed with unique 6-digit HUID traceable on BIS Care portal.
                    </p>
                  </div>

                  <div style={{ padding: '16px', borderRadius: '4px', border: '1px solid #dcfce7', backgroundColor: '#f0fdf4' }}>
                    <div style={{ fontSize: '1.4rem', marginBottom: '6px' }}>🚚</div>
                    <h3 style={{ fontSize: '0.96rem', fontWeight: 700, color: '#166534', margin: '0 0 4px' }}>
                      Armored Vault Delivery
                    </h3>
                    <p style={{ fontSize: '0.82rem', color: '#14532d', margin: 0, lineHeight: 1.4 }}>
                      Dispatched via BVC Logistics armored vehicles with 100% transit insurance.
                    </p>
                  </div>
                </div>

                {/* Direct Concierge Call */}
                <div style={{
                  marginTop: '1.5rem',
                  padding: '16px',
                  backgroundColor: '#0d2346',
                  color: '#ffffff',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px', fontSize: '0.98rem', fontWeight: 700, color: '#ffe500' }}>
                      Need Custom Jewelry or Bullion Booking?
                    </h4>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#cbd5e1' }}>
                      Our head jewelry master craftsman is available on WhatsApp & Call for bespoke orders.
                    </p>
                  </div>
                  <a
                    href="https://wa.me/919829012345?text=Hello%20ML%20Jewellers%2C%20I%20am%20a%20registered%20patron%20and%20need%20assistance."
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      backgroundColor: '#25D366',
                      color: '#ffffff',
                      padding: '8px 16px',
                      borderRadius: '2px',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      textDecoration: 'none'
                    }}
                  >
                    💬 WhatsApp Concierge
                  </a>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
